<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
        <BaseInput
          v-model="modifiedData.name"
          color="base"
          name="name"
          class="sm:text-sm"
          show-error-as-tooltip
          :error="$v.name.required.$invalid ? $v.name.required.$message : ''" />
      </div>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="code" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
        <BaseInput
          v-model="modifiedData.code"
          color="base"
          name="code"
          class="sm:text-sm"
          show-error-as-tooltip
          :error="$v.code.required.$invalid ? $v.code.required.$message : ''" />
      </div>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="users" class="text-sm font-medium">{{ $t('routes.user') }}</label>
        <BaseSelectDropdown
          :items="filteredUsers"
          :selected-item="selectedUser"
          @update:selected="handleUserSelection"
          name="users"
          clearable
          show-error-as-tooltip
          :error="$v.userId.required.$invalid ? $v.userId.required.$message : ''" />
      </div>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="companySite" class="text-sm font-medium">{{ $t('routes.companySite') }}</label>
        <BaseSelectDropdown
          :items="dropdownCompanySites"
          :selected-item="selectedCompanySite"
          @update:selected="handleCompanySiteSelection"
          name="companySite"
          clearable
          show-error-as-tooltip
          :error="$v.companySiteId.required.$invalid ? $v.companySiteId.required.$message : ''" />
      </div>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="routeDefinition" class="text-sm font-medium">{{
          $t('routes.routeDefinition')
        }}</label>
        <BaseSelectDropdown
          :items="dropdownRouteDefinitions"
          :selected-item="selectedRouteDefinition"
          @update:selected="handleRouteDefinitionSelection"
          name="routeDefinition"
          clearable
          show-error-as-tooltip
          :error="
            $v.routeDefinitionId.required.$invalid ? $v.routeDefinitionId.required.$message : ''
          " />
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';

import type { SelectDropdownItem } from '@/types/Base';
import type { Route } from '@/types/Routes';
import { editRoute } from '@/services/api/routes';

import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { deepCopy } from '@/utils/data';

const { t: $t } = useI18n({ useScope: 'global' });
const emit = defineEmits(['closeModal', 'save']);
const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    route: Route;
    activeUsers?: string[];
  }>(),
  { activeUsers: () => [] }
);

const modifiedData = ref<Route>(deepCopy<Route>(props.route));
const isModified = ref<boolean>(false);

onMounted(async () => {
  await getUsersLookup();
  await getCompanySitesLookup();
  await getRouteDefinitionsLookup();
  initDropdowns();
});

async function initDropdowns() {
  if (!modifiedData.value) return;

  const userValue = dropdownUsers.value.find((item) => item.value == modifiedData.value.userId);
  if (userValue) selectedUser.value = userValue;

  const companySiteValue = dropdownCompanySites.value.find(
    (item) => item.value == modifiedData.value.companySiteId
  );
  if (companySiteValue) selectedCompanySite.value = companySiteValue;

  const routeDefinitionValue = dropdownRouteDefinitions.value.find(
    (item) => item.value == modifiedData.value.routeDefinitionId
  );
  if (routeDefinitionValue) selectedRouteDefinition.value = routeDefinitionValue;
}

const { dropdownUsers, dropdownCompanySites, dropdownRouteDefinitions } =
  storeToRefs(useLookupsStore());
const { getUsersLookup, getCompanySitesLookup, getRouteDefinitionsLookup } = useLookupsStore();

const filteredUsers = computed<SelectDropdownItem[]>(() => {
  const active = new Set((props.activeUsers || []).map((id) => String(id)));
  const currentId = modifiedData.value?.userId ? String(modifiedData.value.userId) : '';
  return dropdownUsers.value.filter((u) => {
    const uid = String(u.value);
    if (currentId && uid === currentId) return true;
    return !active.has(uid);
  });
});

const selectedUser = ref<SelectDropdownItem | null>(null);
function handleUserSelection(item: SelectDropdownItem) {
  selectedUser.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.userId = item ? item.value.toString() : '';
  }
}

const selectedCompanySite = ref<SelectDropdownItem | null>(null);
function handleCompanySiteSelection(item: SelectDropdownItem) {
  selectedCompanySite.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.companySiteId = item ? item.value.toString() : '';
  }
}

const selectedRouteDefinition = ref<SelectDropdownItem | null>(null);
function handleRouteDefinitionSelection(item: SelectDropdownItem) {
  selectedRouteDefinition.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.routeDefinitionId = item ? item.value.toString() : '';
  }
}

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.code = newData.code || '';
    validationModel.value.userId = newData.userId || '';
    validationModel.value.companySiteId = newData.companySiteId || '';
    validationModel.value.routeDefinitionId = newData.routeDefinitionId || '';

    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.route);
    isModified.value = valueChanged || $v.value.$invalid;
  },
  { deep: true }
);

function onDismissChanges() {
  modifiedData.value = deepCopy<Route>(props.route);
  emit('closeModal');
}

const toastStore = useToastStore();

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await editRoute(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
    emit('closeModal');
  }
  $v.value.$reset();
}

const validationModel = ref({
  name: modifiedData.value.name || '',
  code: modifiedData.value.code || '',
  userId: modifiedData.value.userId || '',
  companySiteId: modifiedData.value.companySiteId || '',
  routeDefinitionId: modifiedData.value.routeDefinitionId || '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  code: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  userId: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  companySiteId: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  routeDefinitionId: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
